import React, { useEffect, useState } from 'react';
import AppIcon from '../components/AppIcon';
import { BillingPausedBanner } from '../components/billing/BillingCard';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, LayoutDashboard, Wallet, Users, Building2, Calendar, Sparkles, ArrowLeft, ChevronDown, LogOut, Armchair, Mail, Globe, Send, Settings as SettingsIcon, Hotel, Bell, ClipboardCheck, BarChart3, Gift } from 'lucide-react';
import Overview from './dashboard/Overview';
import Budget from './dashboard/Budget';
import Guests from './dashboard/Guests';
import Vendors from './dashboard/Vendors';
import Timeline from './dashboard/Timeline';
import Seating from './dashboard/Seating';
import AIAssistant from './dashboard/AIAssistant';
import SaveTheDate from './dashboard/SaveTheDate';
import WeddingWebsite from './dashboard/WeddingWebsite';
import Invitations from './dashboard/Invitations';
import RsvpEditor from './dashboard/RsvpEditor';
import Insights from './dashboard/Insights';
import Hotels from './dashboard/Hotels';
import Reminders from './dashboard/Reminders';
import Settings from './dashboard/Settings';
import Registry from './dashboard/Registry';
import FloatingAIBubble from '../components/FloatingAIBubble';
import { useAuth } from '../contexts/AuthContext';
import client from '../api/client';

const tabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'insights', label: 'Wedding dashboard', icon: BarChart3 },
  { id: 'budget', label: 'Budget', icon: Wallet },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'vendors', label: 'Vendors', icon: Building2 },
  { id: 'timeline', label: 'Timeline', icon: Calendar },
  { id: 'seating', label: 'Seating', icon: Armchair },
  { id: 'savethedate', label: 'Save the Date', icon: Mail },
  { id: 'invitations', label: 'Invitations', icon: Send },
  { id: 'rsvp', label: 'RSVP page', icon: ClipboardCheck },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'hotels', label: 'Hotels', icon: Hotel },
  { id: 'registry', label: 'Registry', icon: Gift },
  { id: 'website', label: 'Website', icon: Globe },
  { id: 'ai', label: 'AI Friend', icon: Sparkles },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

export default function Dashboard() {
  const [active, setActive] = useState('overview');
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Sync `active` tab (and any deep-link params like ?edit={guestId}) with
  // the URL so cross-tab navigation from KPI drilldowns can jump straight
  // into a specific guest record without full page reload.
  const params = new URLSearchParams(location.search);
  const urlTab = params.get('tab');
  const editGuestId = params.get('edit') || null;
  const editNonce = params.get('t') || null;
  const [msgAlerts, setMsgAlerts] = useState(0);
  useEffect(() => {
    const load = () => client.get('/message-log/alerts').then(({ data }) => setMsgAlerts(data.unseen)).catch(() => {});
    const clear = () => setMsgAlerts(0);
    load();
    const t = setInterval(load, 120000);
    window.addEventListener('message-alerts-seen', clear);
    return () => { clearInterval(t); window.removeEventListener('message-alerts-seen', clear); };
  }, []);

  useEffect(() => {
    if (urlTab && urlTab !== active) setActive(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  const goToTab = (tab, extra = {}) => {
    setActive(tab);
    const qp = new URLSearchParams();
    qp.set('tab', tab);
    Object.entries(extra).forEach(([k, v]) => { if (v) qp.set(k, v); });
    // Bump a nonce so clicking the same guest twice still re-fires the
    // scroll/highlight effect on the Guests tab (React deps stay stable
    // otherwise and the second click would be a no-op).
    if (extra.edit) qp.set('t', String(Date.now()));
    navigate(`/dashboard?${qp.toString()}`, { replace: false });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/profile');
        setProfile(data);
      } catch (e) { /* ignore */ }
      try {
        const { data: status } = await client.get('/onboarding/status');
        if (!status.onboarded) navigate('/onboarding', { replace: true });
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPanel = () => {
    switch (active) {
      case 'overview': return <Overview onNavigate={setActive} />;
      case 'insights': return <Insights onNavigate={goToTab} />;
      case 'budget': return <Budget />;
      case 'guests': return <Guests editGuestId={editGuestId} editNonce={editNonce} />;
      case 'vendors': return <Vendors />;
      case 'timeline': return <Timeline />;
      case 'seating': return <Seating />;
      case 'savethedate': return <SaveTheDate />;
      case 'invitations': return <Invitations />;
      case 'rsvp': return <RsvpEditor />;
      case 'reminders': return <Reminders />;
      case 'hotels': return <Hotels />;
      case 'registry': return <Registry />;
      case 'website': return <WeddingWebsite />;
      case 'ai': return <AIAssistant />;
      case 'settings': return <Settings onNavigate={setActive} />;
      default: return <Overview onNavigate={setActive} />;
    }
  };

  const displayName = profile ? `${profile.partner1 || ''}${profile.partner2 ? ' & ' + profile.partner2 : ''}` : (user?.partner1 || user?.email || '');
  const displayDate = profile?.weddingDate ? new Date(profile.weddingDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Set your date';

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-[#FBF7F2] flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#EFE3D8] bg-[#FBF7F2] sticky top-0 h-screen">
        <div className="p-6 border-b border-[#EFE3D8]">
          <Link to="/" className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#7B2C3B] flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
            </div>
            <span className="font-serif text-lg font-semibold">
              Yourwedding<span className="italic text-[#7B2C3B]">friends</span>
            </span>
          </Link>
          <button onClick={() => navigate('/')} className="text-xs text-[#6A6A6A] hover:text-[#7B2C3B] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to site
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {tabs.map((t) => {
            const isActive = active === t.id;
            return (
              <button key={t.id} data-testid={`sidebar-tab-${t.id}`} onClick={() => goToTab(t.id)} className={`group w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-[background-color,box-shadow,color] duration-200 ${isActive ? 'bg-white text-[#2A2A2A] font-medium shadow-[0_2px_10px_rgba(123,44,59,0.10)] ring-1 ring-[#EFE3D8]' : 'text-[#3A3A3A] hover:bg-white/70'}`}>
                <AppIcon feature={t.id} size="sm" active={isActive} muted={!isActive} interactive /> {t.label}
                {t.id === 'reminders' && msgAlerts > 0 && <span data-testid="sidebar-message-alert-badge" title="Messages that didn't go through" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-semibold flex items-center justify-center">{msgAlerts}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#EFE3D8]">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setActive('settings')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive('settings'); } }}
            data-testid="sidebar-profile-btn"
            className={`soft-card rounded-2xl p-4 w-full text-left cursor-pointer transition-all ${active === 'settings' ? 'ring-2 ring-[#7B2C3B]/40' : 'hover:ring-1 hover:ring-[#EFE3D8]'}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#F4D6CE] flex items-center justify-center text-[#7B2C3B] font-serif font-semibold overflow-hidden">
                {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (displayName || 'Y')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#2A2A2A] truncate">{displayName || 'Your wedding'}</p>
                <p className="text-xs text-[#6A6A6A] truncate">{displayDate}</p>
                <TierBadge user={user} onUpgrade={() => navigate('/#pricing')} />
              </div>
              <SettingsIcon className="w-3.5 h-3.5 text-[#9A8A7E] flex-shrink-0" />
            </div>
          </div>
          <button onClick={handleLogout} data-testid="sidebar-logout-btn" className="mt-2 w-full text-xs text-[#6A6A6A] hover:text-[#7B2C3B] inline-flex items-center justify-center gap-1 py-1">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#FBF7F2]/95 backdrop-blur border-b border-[#EFE3D8]">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#7B2C3B] flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-[#FBF7F2] fill-[#FBF7F2]" />
            </div>
            <span className="font-serif text-base font-semibold">Yourweddingfriend</span>
          </Link>
          <div className="relative">
            <select value={active} onChange={(e) => setActive(e.target.value)} className="appearance-none pr-8 pl-3 py-1.5 rounded-full bg-[#F4E4DA] text-xs text-[#7B2C3B] font-medium border border-[#EBD3C2]">
              {tabs.map((t) => <option key={t.id} value={t.id}>{t.label}{t.id === 'reminders' && msgAlerts > 0 ? ` (${msgAlerts} failed)` : ''}</option>)}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-[#7B2C3B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      <main className="flex-1 min-w-0 lg:pl-0 pt-14 lg:pt-0">
        <div className="p-6 lg:p-10 max-w-6xl">
          <BillingPausedBanner onOpenSettings={() => setActive('settings')} />
          <CountdownRibbon weddingDate={profile?.weddingDate} coupleNames={displayName} tagline={profile?.countdownTagline} />
          {renderPanel()}
        </div>
      </main>

      {active !== 'ai' && <FloatingAIBubble />}
    </div>
  );
}

function CountdownRibbon({ weddingDate, coupleNames, tagline }) {
  if (!weddingDate) return null;
  const d = new Date(weddingDate + 'T00:00:00');
  if (isNaN(d)) return null;
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.ceil((d - now) / msPerDay);
  let big, small;
  if (days < 0) {
    big = 'Congratulations!';
    small = `${coupleNames || 'You'} — hope the day was every bit as magical as you.`;
  } else if (days === 0) {
    big = "It's today.";
    small = "Deep breath. You've got this. We love you.";
  } else if (days === 1) {
    big = '1 day to go';
    small = 'Tomorrow. Set two alarms. Drink water.';
  } else if (days <= 7) {
    big = `${days} days to go`;
    small = "It's the countdown week — everything is falling into place.";
  } else if (days <= 30) {
    const weeks = Math.round(days / 7);
    big = `${days} days to go`;
    small = `About ${weeks} weeks — RSVPs are your friend right now.`;
  } else if (days <= 180) {
    const months = Math.round(days / 30);
    big = `${days} days to go`;
    small = `Roughly ${months} months out — plenty of time, still worth chipping away.`;
  } else {
    const months = Math.round(days / 30);
    big = `${days} days to go`;
    small = `${months} months out — dream big, plan small.`;
  }
  if (tagline && tagline.trim()) small = tagline.trim();
  return (
    <div data-testid="dashboard-countdown" className="mb-6 rounded-2xl border border-[#EFE3D8] bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2] p-4 flex items-center gap-4">
      <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/70 border border-[#EFE3D8] flex flex-col items-center justify-center leading-none">
        <span className="text-[9px] uppercase tracking-widest text-[#7B2C3B]">Days</span>
        <span data-testid="countdown-days" className="font-serif text-2xl text-[#2A2A2A]">{Math.max(0, days)}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-lg text-[#2A2A2A] leading-tight">{big}</p>
        <p className="text-xs text-[#6A6A6A] mt-0.5 truncate">{small}</p>
      </div>
      <Heart className="w-4 h-4 text-[#7B2C3B] fill-[#7B2C3B] flex-shrink-0 hidden sm:block" />
    </div>
  );
}


// Small tier pill shown under the couple's names in the sidebar.
// Sweetheart couples see an inline "Upgrade" link that jumps to /#pricing.
const TIER_LABELS = { sweetheart: 'Sweetheart', beloved: 'Beloved', concierge: 'Concierge', wedding_pass: 'Wedding Pass' };
function TierBadge({ user, onUpgrade }) {
  const [state, setState] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/plan/usage');
        setState(data);
      } catch { /* ignore */ }
    })();
  }, []);
  if (!state) return null;
  const plan = state.plan;
  const label = TIER_LABELS[plan] || plan;
  const isFree = plan === 'sweetheart' || plan === 'free';
  return (
    <div className="mt-1 inline-flex items-center gap-1 text-[10px]" data-testid="sidebar-tier-badge">
      <span className={`px-1.5 py-0.5 rounded-full uppercase tracking-widest ${isFree ? 'bg-[#F4E4DA] text-[#7B2C3B]' : 'bg-[#2A1F1B] text-[#EFC9B0]'}`}>{label}</span>
      {isFree && (
        <button onClick={(e) => { e.stopPropagation(); onUpgrade && onUpgrade(); }} className="text-[10px] text-[#7B2C3B] hover:underline" data-testid="sidebar-upgrade-link">Upgrade</button>
      )}
    </div>
  );
}
